/** Hosted-page OAuth for the Watchtower Console (SIWE happens on Guardius's
 *  /auth/authorize page). The SPA owns only the public-client legs: PKCE,
 *  redirect, and code exchange — no client secret ever lives here.
 *
 *  Access + refresh tokens live in sessionStorage: never localStorage, cleared
 *  on tab close. This survives a page refresh without a full re-auth round-trip
 *  while keeping the tokens out of long-lived storage.
 *
 *  Watchtower tokens must be minted for the Guardius MANAGEMENT tenant so the
 *  token's `aud` matches what the Watchtower Lambdas require. The console
 *  therefore always supplies a concrete `tenant_id` from runtime config to the
 *  authorize URL. All values (API base, Guardius base, tenant) come from
 *  deploy-time runtime config.json. */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  buildAuthorizeUrl,
  buildEndSessionUrl,
  decodeJwtClaims,
  generatePkce,
  permissionMatches,
  randomState,
  PLATFORM_ID,
  READ_PERMISSION,
} from './oauth';
import { isRefreshTokenTombstoned, tombstoneRefreshToken } from './refresh-tombstone';
import {
  clearServerOffset,
  learnServerOffsetFromIat,
  loadServerOffset,
  serverNowMs,
} from './server-clock';

export interface RuntimeConfig {
  apiUrl: string;
  guardiusUrl: string;
  /** The Guardius management tenant UUID. Supplied to the authorize URL so the
   *  minted token's `aud` matches what the Watchtower Lambdas require. */
  tenantId: string;
  env: string;
}

export interface AuthContextValue {
  config: RuntimeConfig;
  token: string | null;
  address: string | null;
  /** Client-side read gate: true when the token carries the usage:read
   *  permission (or its permissions list is truncated — permissive, since the
   *  Lambdas' Guardius authorizer is authoritative and will 403 an unauthorized
   *  read). UI gating only. */
  canRead: boolean;
  /** Set true when a data-fetch is refused by the server with a 403 (the API
   *  authorizer is authoritative and can deny a read the client-side `canRead`
   *  gate let through). The Gate renders NoAccess while this is set. Reset by
   *  `signOut()` so a fresh login re-evaluates. */
  serverDenied: boolean;
  /** Flip `serverDenied` on — called by the API layer when a fetch 403s. */
  denyAccess(): void;
  /** Callback / exchange error surfaced for the sign-in UI to render. */
  error: string | null;
  signIn(): Promise<void>;
  signOut(): void;
  /** End the session EVERYWHERE, for a user-initiated sign-out (#200).
   *
   *  `signOut()` is the LOCAL half only, and alone it is a security defect on
   *  a shared browser: Guardius's hosted SSO session is a host-only,
   *  `SameSite=Lax` cookie living up to 12 h, so the next person to click
   *  "Sign in" performs a top-level navigation — exactly the case that DOES
   *  carry a `Lax` cookie — Guardius's session-skip answers the PREVIOUS user
   *  with a silent `302` carrying a code, and the PKCE exchange mints tokens
   *  for that account. The new person presented no credential and the UI
   *  showed the sign-out as successful.
   *
   *  So this does three things `signOut()` cannot: it revokes the refresh
   *  token server-side (`POST /auth/logout`), it ends the hosted SSO session
   *  (a top-level navigation to `GET /auth/end-session` — the navigation is
   *  the only mechanism because `OPTIONS /auth/logout` returns no
   *  `Access-Control-Allow-Credentials`, so the browser will not attach the
   *  session cookie to a cross-origin `fetch` even when asked with
   *  `credentials: 'include'`; `SameSite=Lax` is NOT the blocker, since these
   *  two hosts share one registrable domain and are merely cross-ORIGIN, so
   *  Lax permits it — full chain in `AGENTS.md`), and it does both AFTER the
   *  local teardown so the UI still responds at once.
   *
   *  USER-INITIATED SIGN-OUT ONLY. An involuntary ending — a refusal from the
   *  rotation (`killSession`), an unmount, anything the reader did not ask
   *  for — must keep calling `signOut()`: navigating a reader away and ending
   *  the hosted Guardius session is not an answer to a server error.
   *
   *  Resolves only once the navigation has been issued; in a real browser the
   *  page is leaving by then. Rejects only if the local teardown or the
   *  navigation itself throws — neither the tombstone write nor the logout
   *  POST can reject it, which is what `Promise.allSettled` buys.
   *
   *  #211: NOT re-entrant, and calling it again while a run is in flight is
   *  safe — the second call runs no body and hands back the first run's own
   *  promise, so a double click navigates once and the first run's awaited
   *  work is never cut short. */
  endSession(): Promise<void>;
  /** Return the BEST token available for the next API request: one valid for
   *  at least EXPIRY_SKEW_MS where it can get one — refreshing silently if the
   *  current one is missing or about to expire — else an access token that is
   *  merely still UNEXPIRED, else `null`.
   *
   *  That middle rung is #194 delta 4, and it exists for the v2.1 SOFT END:
   *  after one, the refresh token is gone forever while the access token
   *  Guardius issued beside it still verifies on its own signature, and v2.1
   *  requires keeping it until its own `exp`. Reporting `null` with a token in
   *  hand that the API would still accept is what turned every SOFT END into a
   *  sign-out one layer up.
   *
   *  Resolves to `null` when the session is dead (no refresh token AND no
   *  unexpired access token, or the refresh was REFUSED → user is signed out),
   *  when a rate-limited refresh could not produce one while the session is
   *  still very much alive (a 429/408 and its one retry — #188), and when a
   *  SOFT END's kept access token has itself expired. `null` therefore means
   *  "no usable token right now", never "signed out": nothing may clear state
   *  on it. A token whose `exp` does not decode is never handed back — we
   *  cannot say it verifies. The API client awaits this before attaching the
   *  `Authorization` header, and fails the request rather than sending an
   *  empty bearer. */
  ensureFreshToken(): Promise<string | null>;
}

// sessionStorage keys. `watchtower.oauth.pending` carries the one-shot PKCE
// verifier + CSRF state + redirect URI, written by signIn() and consumed
// exactly once by the boot callback. `watchtower.token` holds the access token.
// `watchtower.refresh` holds the rotating refresh token (same posture: never
// localStorage, cleared on tab close). Persisted on every grant — the
// authorization_code exchange and each refresh rotation — and consumed by the
// single-flight `refreshSession`. Guardius rotates this on every use with
// reuse-detection, so the OLD value MUST be overwritten the moment a new one
// arrives (a second use of a spent token, outside the 60 s grace window the
// #185 block below describes, revokes ALL the user's sessions).
const PENDING_KEY = 'watchtower.oauth.pending';
const TOKEN_KEY = 'watchtower.token';
const REFRESH_KEY = 'watchtower.refresh';

// Refresh the access token this far ahead of its `exp`. Covers residual clock
// skew plus the round-trip, so a request never carries a token that expires in-flight. A
// background-throttled timer or a slept laptop can blow past this; the
// visibility-change top-up and the per-request `ensureFreshToken` recover those.
//
// That in-flight margin is DELIBERATELY SPENT on one rung (#194 delta 4): after
// a v2.1 SOFT END there is no refresh token left to buy it back, so the kept
// access token is used down to its own `exp` rather than to `exp - this`, and a
// request that starts with 5 s left can still expire in flight. v2.1 says
// "until its `exp`", not "until `exp - skew`", so that is the contract and not
// a gap here — the residual (an in-flight expiry reaching the 401 sign-out) is
// narrowed by the re-auth work, which fires before `exp` for exactly this
// reason. Narrowed, not closed: see REAUTH_GUARD_MS below. (It used to add
// "which cannot help a client clock running behind Guardius" — no longer true
// since #289: every `exp` comparison here runs on `serverNowMs()`, Guardius's
// clock as learned from each arriving token's `iat`.)
// The guard it uses is deliberately NOT this constant: this equals the whole of
// a kept token's remaining life, so a re-auth armed at it would fire at once
// and throw away all the runway the SOFT END just preserved.
export const EXPIRY_SKEW_MS = 60_000;

// How far BEFORE a kept access token's `exp` the post-SOFT-END re-auth fires
// (#194). v2.1 rule 2: re-authenticate "at the app's natural point, at the
// latest at `exp`".
//
// THIS IS A RACE GUARD, NOT RUNWAY MANAGEMENT, AND MUST NOT BE GROWN INTO ONE.
// Firing exactly AT `exp` leaves one window open: a request that starts just
// before `exp` completes just after, the API answers `401`, and `queries.ts`'s
// `if (e.status === 401) signOut()` hard-signs-out — the SOFT END undone at the
// last second, which is the entire failure this outcome exists to prevent. 5 s
// is large enough that we stop STARTING requests before the token dies and
// small enough to be noise against the ≤ EXPIRY_SKEW_MS a SOFT END leaves (a
// refresh only proceeds inside that skew, so a kept token never has more).
//
// Do NOT reach for EXPIRY_SKEW_MS as the guard: it equals the whole of a kept
// token's remaining life, so the re-auth would fire immediately and discard
// every second of the runway the SOFT END exists to preserve. If the race
// still shows up in practice the answer is a request-level retry, not a bigger
// guard here.
//
// ~~One residual this does NOT close, and nothing client-side can: a client
// clock running behind Guardius makes a token we compute as unexpired already
// expired server-side.~~ FALSE since #289, for the CLOCK half: every `exp`
// comparison in this file is made against `serverNowMs()`, the local clock
// plus an offset learned from each freshly arrived token's `iat`
// (`server-clock.ts`), so a device running behind Guardius no longer computes
// a dead token as live. What remains is the offset's own error — network
// latency plus `iat`'s one-second floor, both making a token look up to ~1–2 s
// fresher than it is, which this 5 s guard covers — and a device clock that
// JUMPS after the offset was learned, which nothing re-learns until the next
// token arrives. The guard NARROWS the 401 path; it does not eliminate it.
export const REAUTH_GUARD_MS = 5_000;

// The two refresh answers that are NOT terminal (Guardius client contract,
// 2026-10-24, rule 5): a `429` from the per-(IP, subject) rate limit on
// POST /auth/token, and a `408`. Neither spends the refresh token, so the
// session survives and the SAME token is re-sent once. Every other 4xx still
// kills the session — a retried `401` would be the reuse that revokes every
// session the user has. A 5xx and a rejected `fetch` are NOT terminal either,
// but for a different reason and on a different budget: see the #185 block
// below (the lost-reply class, bounded by the server's grace window).
const REFRESH_RETRYABLE_STATUS: ReadonlySet<number> = new Set([408, 429]);

// How long to wait before the one retry when `Retry-After` is absent or
// unparseable (it is advisory and may be a date, a blank or junk — never NaN
// into a setTimeout).
export const REFRESH_RETRY_DEFAULT_MS = 2_000;

// Ceiling on the wait, whatever the header asked for. The retry is awaited
// INSIDE the single-flight promise — that is what keeps it from becoming a
// second holder of the refresh token (#184) — so an unclamped `Retry-After:
// 86400` would park every API request in the tab behind it. 40s matches the
// client contract's overall retry budget.
export const REFRESH_RETRY_MAX_MS = 40_000;

/** `Retry-After` in ms, clamped, with the default for absent/unparseable. Only
 *  ever called inside the retryable-status branch. */
function refreshRetryDelayMs(res: Response): number {
  const raw = res.headers?.get?.('retry-after');
  const seconds = raw === null || raw === undefined || raw.trim() === '' ? NaN : Number(raw);
  const ms =
    Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : REFRESH_RETRY_DEFAULT_MS;
  return Math.min(ms, REFRESH_RETRY_MAX_MS);
}

// ---------------------------------------------------------------------------
// #185 — retry the refresh answers Guardius tells us to retry, inside its
// 60-second grace window.
//
// Guardius server 2.127.7 (`server/src/shared/pure/refresh-reuse.ts`) grants a
// GRACE: a refresh token a rotation already spent, presented again within
// REFRESH_REUSE_GRACE_SECONDS = 60 of that rotation, supersedes the successor
// the server had minted and yields a fresh pair. A grace leaves the presented
// token's row `revokedReason: 'rotated'` with its ORIGINAL `rotatedAt`
// untouched, so the same token may be graced REPEATEDLY inside the window —
// that is the fact that makes a backoff legal at all.
//
// Outside the window the verdict is `theft`, NOT "invalid":
// `revokeAllForSubject` + `401 TOKEN_REUSED`, killing every refresh token the
// user holds on every device. The 60 s boundary is therefore a SAFETY
// property, not a politeness budget — a retry one second late is catastrophic,
// while a retry that never happens merely signs one tab out. Every wait below
// that could precede a re-presentation is checked against a deadline for that
// reason.
//
// Two answer classes reach the retry:
//
//   - the two documented 503 codes, retried ONCE, mirroring Guardius's own
//     reference client (`sdk/typescript/src/core/refresh-retry.ts` at commit
//     a39f9f59): status 503 AND one of the two codes, 1000 ms default, 5000 ms
//     ceiling, `Retry-After` parsed with /^\d+$/;
//   - the LOST REPLY class: a rejected/aborted `fetch`, or any other 5xx.
//     Ruling (Greg, 2026-10-24): an unspecified 5xx is a lost reply, not a
//     terminal answer. The client contract names only 4xx as terminal and is
//     silent on a 500, a 502 or a 503 with no usable code, and those are the
//     same epistemic situation as a dropped connection — the server may or may
//     not have rotated, and inside the grace window re-presenting is safe
//     either way (graced if it rotated, plainly live if it did not). Signing a
//     user out on a load-balancer blip is the wrongness #185 is about. Cost if
//     that reading is wrong: a little extra load on Guardius during an outage,
//     bounded by the budget below and six attempts.

/** The two `code` values Guardius documents as retryable on a 503, with the
 *  same refresh token. Mirrors their SDK's REFRESH_RETRY_CODES. */
export const REFRESH_GRACE_CODES: ReadonlySet<string> = new Set([
  'REFRESH_ROTATION_INCOMPLETE',
  'TRANSACTION_CONFLICT',
]);

/** Wait before the one 503 retry when `Retry-After` is absent or unusable.
 *  1000 ms because that is their SDK's REFRESH_RETRY_DEFAULT_MS — both sides
 *  of this exchange were written by Guardius and must not disagree. (Not
 *  REFRESH_RETRY_DEFAULT_MS, which is #188's 2000 ms for the 429 path: that
 *  one waits out a rate limit, this one waits out a half-finished rotation.) */
export const REFRESH_GRACE_RETRY_DEFAULT_MS = 1_000;

/** Ceiling on that wait, whatever `Retry-After` asked for: their SDK's
 *  REFRESH_RETRY_MAX_MS. Far tighter than #188's 40 s ceiling because this
 *  wait is spent INSIDE the 60 s grace window. */
export const REFRESH_GRACE_RETRY_MAX_MS = 5_000;

/** The lost-reply backoff, from the client contract (Greg, 2026-10-24):
 *  "re-send the SAME token on 1/2/4/8/16 s backoff, max 40 s from the first
 *  attempt, then sign out". Five entries = five re-sends after the first
 *  attempt (six POSTs at most), summing to 31 s of waiting — inside the
 *  budget below, so the budget binds only when an attempt is itself slow. */
export const REFRESH_LOST_REPLY_BACKOFF_MS: readonly number[] = [
  1_000, 2_000, 4_000, 8_000, 16_000,
];

/** How long after the first AMBIGUOUS attempt started we may still present the
 *  token again. 40 s inside the server's 60 s, i.e. 20 s of margin for the
 *  in-flight POST and for clock skew between this tab and Guardius. */
export const REFRESH_GRACE_BUDGET_MS = 40_000;

/** Per-POST timeout. Browser `fetch` has none, and the whole attempt sequence
 *  now runs inside the Web Lock (#184) — a hung POST would park every other
 *  tab's refresh behind it indefinitely, and would burn the grace window while
 *  doing nothing. An abort surfaces as a rejection, i.e. the lost-reply class,
 *  which is the correct classification: we do not know whether it rotated. */
export const REFRESH_ATTEMPT_TIMEOUT_MS = 10_000;

/** `Retry-After` in ms for the 503 grace path, parsed and clamped EXACTLY as
 *  Guardius's own client does: only all-digit delta-seconds count, so an
 *  HTTP-date, a decimal or junk all fall back to the default. */
function graceRetryDelayMs(res: Response): number {
  const raw = res.headers?.get?.('retry-after');
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (!/^\d+$/.test(trimmed)) return REFRESH_GRACE_RETRY_DEFAULT_MS;
  return Math.min(Number(trimmed) * 1000, REFRESH_GRACE_RETRY_MAX_MS);
}

/** The top-level `code` of a failure body, or undefined when the body is not
 *  readable JSON or carries no string `code`. A gateway's HTML 503 must become
 *  `undefined` (→ lost reply), never an exception. */
async function failureCode(res: Response): Promise<string | undefined> {
  try {
    const body: unknown = await res.json();
    if (!body || typeof body !== 'object') return undefined;
    const code = (body as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  } catch {
    return undefined;
  }
}

/** How long a deliberate sign-out may wait on `POST /auth/logout` before
 *  giving up and navigating anyway (#200).
 *
 *  Short on purpose. Guardius revokes the refresh token and the hosted SSO
 *  session BEFORE it validates the return address and before its own
 *  rate-limit gate, and it answers `200` for almost everything — so a slow
 *  answer means a slow network, not a refusal, and there is nothing a longer
 *  wait would learn. The reader clicked sign out and must not be held on a
 *  signed-in-looking UI while a down Guardius times out at its own pace
 *  (browser `fetch` has no timeout of its own). */
export const END_SESSION_LOGOUT_TIMEOUT_MS = 3_000;

/** `AbortSignal.timeout(ms)` where it exists, else undefined — some runtimes
 *  (and some DOM test environments) ship `AbortSignal` without it, and a
 *  missing timeout must degrade to today's untimed POST rather than throw.
 *
 *  Parameterized rather than duplicated: the refresh POST (#185) and the
 *  sign-out logout POST (#200) want different bounds but the same degrade
 *  behaviour, and a second copy of this feature-detect is a second thing to
 *  get wrong. */
function timeoutSignal(ms: number): AbortSignal | undefined {
  if (typeof AbortSignal === 'undefined') return undefined;
  const timeout = (AbortSignal as { timeout?: (ms: number) => AbortSignal }).timeout;
  return typeof timeout === 'function' ? timeout.call(AbortSignal, ms) : undefined;
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------------------
// #184 — one holder per refresh token, across tabs.
//
// `sessionStorage` is per-tab by design, so two independently opened tabs each
// run their own PKCE and hold DISTINCT refresh tokens. But duplicating a tab
// (and Chrome's "reopen closed tab") COPIES `sessionStorage`, so two tabs hold
// the SAME rotating token — and because each tab's proactive timer is derived
// from that one token's `exp`, they are scheduled to fire at the same absolute
// instant. Two simultaneous presentations both clear Guardius's verification
// and take its revoke-all branch: the user is signed out on every device.
//
// The fix is not to detect duplication (a duplicate and a reload are
// indistinguishable from inside the page) but to make the copies cooperate:
//
//   - an origin-wide Web Lock means only ONE tab ever has a refresh in flight;
//   - the token is re-read from storage AFTER the lock is acquired, so a queued
//     tab presents the successor the winner stored, never the token it queued
//     with;
//   - the rotation is published on a BroadcastChannel so sibling copies adopt
//     the successor instead of dying on a spent token.
//
// Both APIs may be absent (older Safari, a non-secure context, some test
// environments). Each is feature-detected and the code degrades to exactly the
// previous behaviour — in-tab single-flight only — rather than throwing.
export const REFRESH_LOCK_NAME = 'watchtower.refresh';
export const AUTH_CHANNEL_NAME = 'watchtower.auth';

/** What a tab publishes to its copies. `spent` is the refresh token the sender
 *  PRESENTED: it is dead by the time the message goes out, and it is what
 *  identifies the receivers that share this sender's token. A receiver whose
 *  stored refresh token differs is a different sign-in (its own PKCE, its own
 *  token) and MUST be left alone — otherwise a tab the user signed out of gets
 *  silently signed back in, possibly as a different wallet. */
//
// #194 adds the third member. `signed-out` and `drop-this-token` differ in
// exactly the way v2.1's HARD END and SOFT END differ: both say "this refresh
// token is dead, stop presenting it", but only `signed-out` says "and the
// session is over". A SOFT END in one tab must not end its copies' sessions —
// their access tokens verify until their own `exp` just as the sender's does.
type AuthBroadcast =
  | { type: 'rotated'; spent: string; accessToken: string; refreshToken: string | null }
  | { type: 'signed-out'; spent: string }
  | { type: 'drop-this-token'; spent: string };

/** The slice of `LockManager` we use. Declared locally so the feature detect
 *  works whether or not the TS DOM lib of the day types `navigator.locks`. */
interface LockManagerLike {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

/** `navigator.locks` when it is really there (it is `null`, not absent, in some
 *  runtimes), else null. */
function lockManager(): LockManagerLike | null {
  const nav: unknown = typeof navigator === 'undefined' ? null : navigator;
  const locks = (nav as { locks?: LockManagerLike | null } | null)?.locks;
  return locks && typeof locks.request === 'function' ? locks : null;
}

/** Run `fn` holding the origin-wide refresh lock, so no other tab in this
 *  origin can have a refresh in flight at the same time. Degrades to a plain
 *  call where the Web Locks API is unavailable. */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = lockManager();
  if (!locks) return fn();
  return locks.request(REFRESH_LOCK_NAME, fn);
}

/** A channel to this origin's other Console tabs, or null where
 *  `BroadcastChannel` is unavailable. */
function openAuthChannel(): BroadcastChannel | null {
  const Ctor = (globalThis as { BroadcastChannel?: typeof BroadcastChannel }).BroadcastChannel;
  if (typeof Ctor !== 'function') return null;
  return new Ctor(AUTH_CHANNEL_NAME);
}

// The OAuth callback path on this origin. The SPA is served at the site root.
const redirectPath = '/';

interface PendingAuth {
  verifier: string;
  state: string;
  redirectUri: string;
  /** Where the reader was when `signIn()` navigated away (#194). v2.1 rule 6:
   *  the re-auth is a TOP-LEVEL navigation — the hosted pages refuse framing —
   *  so after a SOFT END the reader is taken off their page and must be put
   *  back on it.
   *
   *  OPTIONAL, and that is not defensive typing: a stash written by the
   *  previous build and mid-flight across a deploy has no `returnTo`, and so
   *  does one a user hand-edited. The field is read back out of storage and
   *  handed to `history.replaceState`, so it is untrusted input — see
   *  `isRestorablePath`. */
  returnTo?: string;
}

/** Is this stored value safe to hand to `history.replaceState`?
 *
 *  An unvalidated stored path is an open-redirect shape even inside
 *  `replaceState`, so this is a gate and not ceremony. Three rejections, each
 *  for its own reason:
 *
 *    - anything not starting with a single `/` — an absolute URL
 *      ('https://evil.example/x') or a bare relative one ('runs/trading'),
 *      neither of which is this origin's path;
 *    - a second character of `/` — '//evil.example/x' is protocol-relative and
 *      resolves CROSS-ORIGIN;
 *    - a second character of `\` — '/\evil.example/x' resolves cross-origin
 *      too, because the URL parser normalises a backslash to a slash for a
 *      special scheme. It passes "single leading slash, not `//`" and is the
 *      one shape a reader writing this check from the description would miss.
 *
 *  A cross-origin value would make `replaceState` throw `SecurityError`, so
 *  this guard is also what keeps a successful sign-in from being reported as a
 *  failure. */
function isRestorablePath(value: unknown): value is string {
  return (
    typeof value === 'string' && value.startsWith('/') && value[1] !== '/' && value[1] !== '\\'
  );
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Failure kinds for runtime-config loading. The boot sequence (App.tsx) keys
 *  on these to decide between a local-dev fallback and a loud error screen:
 *
 *    - 'not-found' : HTTP 404 — config genuinely absent (a real deploy always
 *                    ships config.json, so 404 means we're in local dev).
 *    - 'http'      : any other non-2xx (403/500/…) — a deployed config that the
 *                    origin refused or failed to serve. Must surface loudly.
 *    - 'parse'     : 2xx but the body isn't valid JSON. `contentType` carries the
 *                    response's Content-Type so callers can disambiguate an
 *                    SPA-fallback index.html (text/html) from a corrupt
 *                    application/json upload.
 *    - 'network'   : the fetch() itself rejected (DNS/offline/CORS). Ambiguous;
 *                    surfaced loudly — a same-origin asset fetch shouldn't reject. */
export type ConfigErrorKind = 'not-found' | 'http' | 'parse' | 'network';

export class ConfigError extends Error {
  constructor(
    public readonly kind: ConfigErrorKind,
    message: string,
    public readonly status?: number,
    public readonly contentType?: string | null,
  ) {
    super(message);
    this.name = 'ConfigError';
  }
}

/** Load deploy-time runtime config injected by the DashboardStack
 *  BucketDeployment (CDK Source.jsonData → /config.json). Throws a typed
 *  {@link ConfigError} carrying the failure kind so the boot sequence can decide
 *  between the local-dev fallback and a loud error screen. */
export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  let res: Response;
  try {
    res = await fetch('/config.json');
  } catch (e) {
    throw new ConfigError(
      'network',
      `config.json fetch failed: ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  if (!res.ok) {
    const kind: ConfigErrorKind = res.status === 404 ? 'not-found' : 'http';
    throw new ConfigError(kind, `config.json load failed: ${res.status}`, res.status);
  }

  try {
    return (await res.json()) as RuntimeConfig;
  } catch (e) {
    throw new ConfigError(
      'parse',
      `config.json is not valid JSON: ${e instanceof Error ? e.message : String(e)}`,
      res.status,
      res.headers.get('content-type'),
    );
  }
}

/** #289: learn Guardius's clock from a token that has JUST ARRIVED from this
 *  tab's own call to the token endpoint. Never call this on a token read back
 *  from storage: its `iat` is up to its whole lifetime old, and learning from
 *  it would shift the clock by the token's age. Nor on a sibling's `rotated`
 *  successor — see the receiver for why. */
function learnClockFrom(accessToken: string): void {
  learnServerOffsetFromIat(decodeJwtClaims(accessToken)?.iat);
}

/** Derive UI-gating fields from a stored token. Returns nulls / false when the
 *  token is absent, malformed, or expired (expired tokens are cleared by the
 *  caller). `canRead` is permissive when permissions are truncated: the server
 *  is authoritative and will 403 an unauthorized read, so we never hide a view
 *  the server might allow. */
export function deriveState(token: string | null): {
  address: string | null;
  canRead: boolean;
  expired: boolean;
} {
  if (!token) return { address: null, canRead: false, expired: false };
  const claims = decodeJwtClaims(token);
  if (!claims) return { address: null, canRead: false, expired: true };
  if (typeof claims.exp === 'number' && claims.exp * 1000 <= serverNowMs()) {
    return { address: null, canRead: false, expired: true };
  }
  const address = claims.wallet_address ?? claims.sub;
  const canRead =
    claims.permissions_truncated === true
      ? true
      : permissionMatches(claims.permissions, READ_PERMISSION);
  return { address, canRead, expired: false };
}

export function AuthProvider({
  config,
  children,
}: {
  config: RuntimeConfig;
  children: ReactNode;
}) {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serverDenied, setServerDenied] = useState(false);

  // One-shot guard for the OAuth callback exchange. Under React StrictMode the
  // boot effect is invoked twice; the first invoke consumes the one-shot pending
  // stash and starts the code exchange, so the second invoke MUST NOT re-enter
  // the callback path (the stash is gone — re-entry would spuriously reject as a
  // state mismatch and clobber a successful sign-in). This ref survives the
  // double-invoke (a module-level flag would leak across remounts/tests, so we
  // scope it per-provider). Because the stash is single-use, a second exchange
  // is impossible, so the resolved token is committed to state regardless of the
  // effect-cleanup `cancelled` flag.
  const exchangeStarted = useRef(false);

  // Single-flight guard for `refreshSession`. Holds the one in-flight refresh
  // promise so the proactive timer, the visibility top-up, and per-request
  // `ensureFreshToken` calls all share it — two concurrent refreshes would each
  // POST the same rotating refresh token, and the second use trips Guardius's
  // reuse-detection and revokes EVERY session for the user.
  const refreshing = useRef<Promise<string | null> | null>(null);

  // The channel this tab publishes rotations / sign-outs on, and listens to its
  // copies on (#184). Declared FIRST so it is live before the boot effect and
  // the proactive timer below can produce anything worth publishing.
  const channel = useRef<BroadcastChannel | null>(null);

  const publish = (msg: AuthBroadcast): void => {
    channel.current?.postMessage(msg);
  };

  // ---------------------------------------------------------------------------
  // #194 — the re-auth TRIGGER. Without it the SOFT END is a REGRESSION on the
  // hard sign-out it replaces, not an improvement on it.
  //
  // After a SOFT END the refresh token is gone and the access token is kept —
  // and nothing re-arms the proactive timer below, because that timer is
  // derived from React `token` state and a SOFT END deliberately does not touch
  // it. So the first request after `exp` finds `ensureFreshToken` stale with no
  // refresh token, gets `null`, and `api.ts` throws a plain `Error` (NOT an
  // `ApiError`, so `queries.ts` does not sign out). The reader is then looking
  // at a signed-in UI over a dead session, failing in the console, until
  // something happens to call `signOut`. `killSession()` at least showed them a
  // signed-out state they could act on. This timer is the only thing that will
  // ever fire.
  //
  // One timer per provider, held in a ref rather than state: arming it must not
  // re-render (a SOFT END clears NO user state, and a re-render is user-visible
  // state in every way that matters here).
  const reauthTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledReauth = (): void => {
    if (reauthTimer.current !== null) {
      clearTimeout(reauthTimer.current);
      reauthTimer.current = null;
    }
  };

  /** Arm the post-SOFT-END re-auth off whatever access token storage holds NOW.
   *
   *  Cancel-then-arm, so two SOFT ENDs (or a SOFT END and a `drop-this-token`
   *  from a copy) cannot leave two timers racing to navigate.
   *
   *  Always through `setTimeout`, even at delay 0: an immediate synchronous
   *  `signIn()` would navigate out of the middle of `refreshSession`, and a
   *  timer is the only form an unmount can cancel.
   *
   *  No decodable `exp` means we cannot say how long the token is good for, so
   *  we do not bet on it — re-authenticate at once, as for an already-expired
   *  one. */
  const scheduleReauth = (): void => {
    cancelScheduledReauth();
    const kept = sessionStorage.getItem(TOKEN_KEY);
    const claims = kept ? decodeJwtClaims(kept) : null;
    const expMs = typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
    // `expMs === null` covers THREE cases and they are deliberately one rule:
    // an undecodable kept token, a kept token with no `exp` claim, and no kept
    // access token at all. v2.1 says "at the latest at `exp`"; where there is
    // no `exp` to wait for, "at the latest" is now.
    //
    // The third case — ABSENT — is worth its own note, because it is the one
    // that navigates a tab the reader may be looking at. It is reached when a
    // tab that held only a refresh token (boot clears an expired access token
    // and keeps `REFRESH_KEY`) tries to refresh and the attempt goes ambiguous.
    // That reader was signed in moments ago and expects to still be, so an
    // immediate re-auth is exactly v2.1's "re-authenticate at the app's
    // natural point" — and with a live hosted SSO session it is a silent `302`
    // they never see.
    //
    // What makes auto-navigating safe rather than an override of the user's
    // wishes is `signOut()`: it cancels any pending re-auth AND removes
    // `REFRESH_KEY`, so after a deliberate sign-out there is no refresh token
    // to attempt, no SOFT END to reach here, and nothing to navigate. The
    // trigger can therefore never sign a reader back in against their choice.
    // If that pair of lines in `signOut` is ever separated, re-read this.
    const delay = expMs === null ? 0 : Math.max(0, expMs - serverNowMs() - REAUTH_GUARD_MS);
    reauthTimer.current = setTimeout(() => {
      reauthTimer.current = null;
      // `signIn` is async and does real work before navigating (`generatePkce`
      // awaits `crypto.subtle.digest`), so a scheduled call MUST handle its
      // promise — a rejection inside a `setTimeout` is an unhandled rejection
      // and the reader would be left in exactly the dead-session state this
      // timer exists to prevent, only silently.
      //
      // On a rejection we cannot re-authenticate automatically, so we fall back
      // to the pre-v2.1 ending: a signed-out state the reader can act on, with
      // a message. That is strictly better than a signed-in UI over a session
      // that cannot be renewed. `setError` comes AFTER `signOut`, which nulls
      // it.
      void signIn().catch(() => {
        signOut();
        setError('Your session expired and could not be renewed. Please sign in again.');
      });
    }, delay);
  };

  // Clear the re-auth timer when the provider goes away. A provider torn down
  // by a route change or a test unmount must not drag the window to the
  // authorize endpoint minutes later — the same discipline as the proactive
  // timer's own cleanup, except that this timer outlives any single `token`
  // value and so cannot live in that effect.
  useEffect(() => cancelScheduledReauth, []);

  useEffect(() => {
    const ch = openAuthChannel();
    if (!ch) return;
    channel.current = ch;
    ch.onmessage = (ev: MessageEvent) => {
      const msg = ev.data as AuthBroadcast | null;
      if (!msg || typeof msg !== 'object' || typeof msg.spent !== 'string') return;
      // Apply only to the tabs that share the sender's token — a copy of this
      // storage. A tab holding a different refresh token is a different
      // sign-in and must keep (or keep not having) its own session.
      if (sessionStorage.getItem(REFRESH_KEY) !== msg.spent) return;
      // An exhaustive `switch` rather than an if/else chain, and the `never`
      // default is the point of it: a fourth member added to `AuthBroadcast`
      // would slip silently past an `else if` chain with tsc clean, and the
      // tab would ignore a message it was sent. The default does nothing at
      // RUNTIME (a foreign message from some other script on this origin must
      // never throw out of an event handler) while failing the TYPE-CHECK the
      // moment the union grows.
      switch (msg.type) {
        case 'rotated': {
          if (typeof msg.accessToken !== 'string' || msg.accessToken === '') return;
          // #289: deliberately NO clock learn here. Both tabs share one device
          // clock, so this tab's offset is already right — learned at its own
          // arrival, or inherited when its `sessionStorage` was duplicated.
          // Re-learning on delivery could only ADD the delivery latency, and a
          // frozen background tab can deliver minutes late on resume, biasing
          // the offset by the whole freeze. A tab with no offset yet judges on
          // the device clock until its own next refresh learns one — exactly
          // the pre-#289 behaviour.
          sessionStorage.setItem(TOKEN_KEY, msg.accessToken);
          if (typeof msg.refreshToken === 'string' && msg.refreshToken !== '') {
            sessionStorage.setItem(REFRESH_KEY, msg.refreshToken);
            // #190: the sender spent this value, so record it here too. The
            // sender already did (it awaits its own write), making this a
            // de-dupe in the common case — but the record must not depend on the
            // sender still being alive, and a copy restored LATER reads the
            // store, not the message. Fire-and-forget: nothing below reads it
            // back in this tick.
            //
            // Deliberately INSIDE this `if`, on exactly the condition that
            // stores the successor: a `rotated` message carrying no successor
            // would otherwise tombstone the value this tab still holds and sign
            // it out on its next refresh. Same guard, and same reasoning, as the
            // one on the rotation path in `refreshSession` — see it for why the
            // shape is unreachable today and why the guard is kept regardless.
            void tombstoneRefreshToken(msg.spent);
          }
          setToken(msg.accessToken);
          return;
        }
        case 'signed-out': {
          sessionStorage.removeItem(TOKEN_KEY);
          clearServerOffset();
          sessionStorage.removeItem(REFRESH_KEY);
          setToken(null);
          void tombstoneRefreshToken(msg.spent);
          return;
        }
        // #194 — the sender took a SOFT END on the token this tab also holds.
        // The REFRESH token is dead for everyone who holds it, so drop it and
        // record it; the ACCESS token is not, so `TOKEN_KEY` stays, `setToken`
        // is not called and no user state is cleared. This tab keeps serving
        // requests until its access token expires and then re-authenticates at
        // a natural point, which is the whole difference between v2.1's SOFT
        // END and the sign-out this used to be.
        case 'drop-this-token': {
          sessionStorage.removeItem(REFRESH_KEY);
          void tombstoneRefreshToken(msg.spent);
          // v2.1 rule 5, the clause that completes "shows nothing": a peer that
          // drops a token "schedules its own re-auth". This tab never made the
          // request that produced the SOFT END, so without this line it is in
          // the regression above with nothing at all to warn it.
          //
          // This effect's dependency list is `[]`, so it captures the FIRST
          // render's `scheduleReauth` and through it the first render's
          // `signIn`. That is deliberate and harmless — both close over
          // `config` and the `reauthTimer` ref, none of which change for the
          // life of a provider — and it is the same shape the visibility effect
          // below already accepts for `ensureFreshToken`.
          scheduleReauth();
          return;
        }
        default: {
          const unhandled: never = msg;
          void unhandled;
          return;
        }
      }
    };
    return () => {
      ch.onmessage = null;
      channel.current = null;
      ch.close();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      // #289: restore the Guardius clock offset learned when the stored token
      // ARRIVED, before anything below judges that token's `exp`. Never
      // re-learned from the stored token's `iat` — see `learnClockFrom`.
      loadServerOffset();
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');

      // OAuth error bounce-back: Guardius redirects here with `?error=...` (and
      // no code) when sign-in itself fails.
      const oauthError = params.get('error');
      if (oauthError && !code) {
        stripQuery();
        if (!cancelled) setError('Sign-in failed: ' + oauthError);
        return;
      }

      if (code && state) {
        // Run the callback exchange exactly once across StrictMode's double
        // invoke. The very first invoke that reaches here owns the exchange.
        if (exchangeStarted.current) return;
        exchangeStarted.current = true;

        // Read-and-remove the one-shot pending stash. A missing stash or a
        // state mismatch is a hard reject — we must NOT exchange the code.
        const raw = sessionStorage.getItem(PENDING_KEY);
        sessionStorage.removeItem(PENDING_KEY);
        stripQuery();

        let pending: PendingAuth | null = null;
        try {
          pending = raw ? (JSON.parse(raw) as PendingAuth) : null;
        } catch {
          pending = null;
        }

        if (!pending || pending.state !== state) {
          setError('Sign-in could not be verified (state mismatch). Please try again.');
          return;
        }

        try {
          const res = await fetch(`${config.guardiusUrl}/auth/token`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              grant_type: 'authorization_code',
              code,
              redirect_uri: pending.redirectUri,
              code_verifier: pending.verifier,
              platform_id: PLATFORM_ID,
            }),
          });
          if (!res.ok) {
            setError(`Token exchange failed (${res.status}).`);
            return;
          }
          const body = (await res.json()) as {
            access_token?: string;
            refresh_token?: string;
          };
          if (!body.access_token) {
            setError('Token exchange returned no access token.');
            return;
          }
          // Silent refresh: persist the rotating refresh_token so the proactive
          // timer / per-request `ensureFreshToken` can keep this session alive
          // past the access token's lifetime without a re-sign-in.
          learnClockFrom(body.access_token);
          sessionStorage.setItem(TOKEN_KEY, body.access_token);
          if (body.refresh_token) sessionStorage.setItem(REFRESH_KEY, body.refresh_token);
          // Commit regardless of `cancelled`: the exchange is one-shot, so the
          // StrictMode cleanup that flipped `cancelled` is followed by a second
          // invoke that no-ops on the guard above — committing here is the only
          // path that lands the token in state.
          setToken(body.access_token);
          // #194 — put the reader back where they were. AFTER the session is
          // committed, because the URL is cosmetic and the token is not, and in
          // its OWN try/catch: `replaceState` throws `SecurityError` for a
          // cross-origin URL, and the surrounding catch would turn a SUCCESSFUL
          // sign-in into `setError('…')`. `isRestorablePath` should make that
          // unreachable; the try is there because it is the cheaper of the two
          // ways to be sure.
          //
          // A stash with no `returnTo` — the previous build's, mid-flight
          // across a deploy — is ignored and the reader lands at the root,
          // exactly as before this change.
          if (isRestorablePath(pending.returnTo)) {
            try {
              window.history.replaceState({}, '', pending.returnTo);
            } catch {
              // Already signed in and already at the root: nothing to report.
            }
          }
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Token exchange failed.');
        }
        return;
      }

      // No callback: load any persisted token, clearing it if expired.
      const stored = sessionStorage.getItem(TOKEN_KEY);
      if (stored) {
        const { expired } = deriveState(stored);
        if (expired) {
          sessionStorage.removeItem(TOKEN_KEY);
          clearServerOffset();
          if (!cancelled) setToken(null);
        } else if (!cancelled) {
          setToken(stored);
          // A RELOAD DURING THE SOFT END RUNWAY (#194, whole-branch review).
          // An access token with no refresh token beside it is exactly the
          // storage shape a SOFT END leaves behind — and `reauthTimer` died
          // with the previous provider. Without this line the reader is
          // restored onto a signed-in UI over a session that will be dead at
          // `exp`, failing only in the console: the state a SOFT END exists to
          // avoid, and strictly worse than the hard sign-out it replaced.
          //
          // No new arithmetic: `scheduleReauth` reads storage rather than
          // state and clamps a dead or undecodable token to 0 by itself.
          if (!sessionStorage.getItem(REFRESH_KEY)) scheduleReauth();
        }
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, [config]);

  // Single-flight refresh-token rotation. FOUR outcomes after #194, which is
  // Guardius refresh pattern v2.1 (agreed 2026-10-24 across Saga, Watchtower
  // and Penseur OS) — the shipped v1 had only two endings:
  //
  //   RENEWED   a 200 carrying an access token → the new token.
  //   KEEP      a 408/429 with nothing ambiguous before it → both tokens kept,
  //             one clamped retry (#188), then `null` meaning "no usable token
  //             right now", never "signed out".
  //   SOFT END  every AMBIGUOUS give-up — budget exhausted, backoff spent,
  //             attempt timeout, the unspecified-5xx run out, a 408/429 after
  //             an ambiguity, a tombstoned token → `softEnd()`: the refresh
  //             token is discarded (and tombstoned, origin-wide, forever) and
  //             NOTHING else is touched. The access token still verifies until
  //             its `exp`, so it is kept — and returned to the caller while it
  //             is still unexpired (see `softEnd`: past `exp` the answer is
  //             `null`, and `TOKEN_KEY` is still not cleared).
  //   HARD END  a REFUSAL — any 4xx that is not 408/429, `401 TOKEN_REUSED`
  //             above all, and a 200 that yields no access token →
  //             `killSession()`: both tokens cleared, state nulled.
  //
  // Returns the new access token, the surviving one on a SOFT END while that
  // one is still unexpired, or null on a HARD END, a still-rate-limited KEEP,
  // and a SOFT END whose access token has itself expired by then. One more
  // `null` exit since #210: a 200 that lands after the session ended is
  // DISCARDED — see the guard on the rotation path for why, and why `null`
  // rather than the new token (an `ensureFreshToken` awaiting this promise
  // would otherwise send one more request on an ended session). All
  // callers funnel through the `refreshing` ref so only one POST is ever in
  // flight against the rotating refresh token — every retry wait, #188's
  // rate-limit one and #185's grace / lost-reply ones alike, is awaited INSIDE
  // that promise for the same reason.
  const refreshSession = (): Promise<string | null> => {
    if (refreshing.current) return refreshing.current;

    const run = async (): Promise<string | null> =>
      // The lock spans the WHOLE attempt SEQUENCE — every retry wait and every
      // later POST included: releasing in between would let a sibling copy
      // acquire it and present the same token concurrently, which is the
      // collision this is here to prevent. The window to hit is now the whole
      // sequence: up to REFRESH_RETRY_MAX_MS for #188's one rate-limit wait,
      // plus #185's lost-reply backoff bounded by REFRESH_GRACE_BUDGET_MS.
      withRefreshLock(async (): Promise<string | null> => {
        // Re-read BOTH tokens now that the lock is held. This is the whole
        // point of the lock: while this tab was queued, the tab that held it
        // may have rotated the token and published the successor, which this
        // tab's `onmessage` wrote into storage. Presenting the value captured
        // before the wait would be a genuine reuse of a spent token — the lock
        // would then serialise the collision instead of preventing it.
        const current = sessionStorage.getItem(TOKEN_KEY);
        const claims = current ? decodeJwtClaims(current) : null;
        const expMs = typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
        // The winner already did the work: its access token is good for longer
        // than the skew, so there is nothing to refresh. Rotating again here
        // would double every tab's traffic into the very rate limit #188 is
        // about, and each extra rotation re-arms every copy's timer.
        if (current && expMs !== null && expMs - serverNowMs() > EXPIRY_SKEW_MS) return current;

        const refreshToken = sessionStorage.getItem(REFRESH_KEY);
        if (!refreshToken) return null;

        // HARD END (v2.1). Reached ONLY from a REFUSAL now: a 4xx other than
        // 408/429, `401 TOKEN_REUSED` above all, and a 200 we cannot turn into
        // an access token. Those are answers, not ambiguities — the server has
        // told us this credential is finished, so there is nothing left to keep
        // and the session is genuinely over.
        //
        // Every ambiguous give-up goes to `softEnd` below instead. Before #194
        // they came here too, and the paragraph that used to sit in this
        // comment — about the tombstoned token possibly still being LIVE —
        // moved there with them, because it is a statement about ambiguity and
        // this function no longer sees any.
        //
        // The scoping that still matters here: the 429-without-ambiguity exit
        // returns `null` WITHOUT calling this, so a merely rate-limited
        // session — alive, both tokens kept — is never tombstoned.
        const killSession = async (): Promise<null> => {
          // A refusal is an ANSWER, so the session is genuinely over and the
          // reader gets the signed-out state and chooses. Nothing here arms a
          // re-auth — and any re-auth an EARLIER SOFT END armed is cancelled,
          // because navigating to the authorize endpoint after the server
          // refused this credential would re-sign-in a session it just ended.
          cancelScheduledReauth();
          sessionStorage.removeItem(TOKEN_KEY);
          clearServerOffset();
          sessionStorage.removeItem(REFRESH_KEY);
          setToken(null);
          // #190: this value is discarded, so record it before anything can
          // replay it.
          //
          // AWAITED, for the reason the rotation path's write is awaited: a
          // pending microtask dies with the tab, and this is reached at the
          // moment a user is most likely to close it. Every caller already
          // `return`s this from an async function, so the await costs one
          // microtask on a path that is ending anyway — against leaving a
          // token unrecorded for a later tab restore to replay, which is #190
          // reopened by a race. (It was fire-and-forget when #190 shipped;
          // changed with `softEnd`'s, where the same argument is stronger.)
          await tombstoneRefreshToken(refreshToken);
          // Tell the copies: a tab left holding this dead token would otherwise
          // keep presenting it.
          publish({ type: 'signed-out', spent: refreshToken });
          return null;
        };

        // SOFT END (v2.1) — the outcome #194 adds, and the reason it is safe is
        // one sentence: the only dangerous act after a lost refresh reply is
        // PRESENTING THAT TOKEN AGAIN once its rotation is more than the
        // server's grace window old. Discarding it is always safe. So "the
        // budget is spent" means *never send this token again*; it never had to
        // mean *end the session now*, and v1 conflated the two.
        //
        // What that buys: the access token in storage was issued by Guardius
        // and verifies on its own signature until its `exp` — a refresh that
        // could not be completed says nothing about it. Ending the session here
        // threw away minutes of working session and made the user re-authorize
        // over a load-balancer blip, which is the wrongness v2.1 names.
        //
        // So: the refresh token and nothing else. `TOKEN_KEY` stays, `setToken`
        // is not called, `setError` is not set, no user state is cleared, and
        // the caller is handed the access token that is still good. The
        // session re-authenticates at a natural point instead — when that
        // access token expires.
        //
        // The discard is made PERMANENT and ORIGIN-WIDE by the #190 tombstone,
        // which is exactly what v2.1 requires of a SOFT END: a copy of this
        // `sessionStorage` restored tomorrow must not replay this value.
        //
        // The token recorded here may still be LIVE — this is the paragraph
        // that moved out of `killSession`. An exhausted lost-reply backoff, or
        // a tab that was offline for all of it, arrives without knowing whether
        // the server ever received anything. Tombstoning it anyway is the right
        // direction and costs nothing that was not already lost: we have just
        // decided never to present it again, so recording that decision adds
        // no loss. It does mean a later RESTORE of this storage snapshot
        // re-authenticates rather than resuming, which is #190's bargain.
        //
        // One clause of v2.1 is deliberately NOT implemented and must never be:
        // it permits KEEP (hold the refresh token) on "a positive signal that
        // the request never left" — failing before any request byte was
        // written. A browser cannot observe that. `fetch` rejects with an
        // opaque `TypeError` that cannot distinguish a refused connect from a
        // reply lost after the server rotated, and `navigator.onLine` reports a
        // network interface, which the contract itself rules out as a signal.
        // So in the Console that clause is permanently "never": EVERY ambiguous
        // failure is a SOFT END. Claiming the signal we do not have is the one
        // direction that costs revoke-all on every device.
        // AWAITED, and here the argument is STRONGER than on the rotation
        // path, not weaker. A SOFT END is reached after up to 31 s of
        // lost-reply backoff — precisely the moment a user gives up and closes
        // the tab — and afterwards the tab keeps working silently rather than
        // showing a signed-out screen, so nothing on screen would make anyone
        // wait. A dropped microtask there leaves a maybe-rotated token
        // unrecorded for a later restore to replay: #190 reopened by a race.
        const softEnd = async (): Promise<string | null> => {
          await tombstoneRefreshToken(refreshToken);
          sessionStorage.removeItem(REFRESH_KEY);
          // Tell the copies to do the same — drop the shared refresh token,
          // keep their own access tokens, stay signed in.
          publish({ type: 'drop-this-token', spent: refreshToken });
          // Read back rather than returning the value captured when the lock
          // was acquired: a sibling may have published a successor since, and
          // what the caller wants is whatever access token storage holds NOW.
          //
          // Returned only while it is still UNEXPIRED (#194 delta 4). v2.1
          // says "keep using the access token until its `exp`" — until, not
          // past. The expiry check is not pedantry: this exit is reached after
          // up to 31 s of lost-reply backoff, or past the whole 40 s grace
          // budget on the 503 path, so an access token that was merely inside
          // EXPIRY_SKEW_MS when the run started can be genuinely dead by the
          // time we get here. Handing that one back would have `api.ts` SEND
          // it, the API answer `401`, and `queries.ts`'s `if (e.status === 401)
          // signOut()` hard-sign-out — the SOFT END undone by the layer above
          // it, which is the exact failure this outcome exists to prevent. A
          // token with no decodable `exp` answers `null` too: we cannot say it
          // verifies.
          //
          // `TOKEN_KEY` is NOT removed on that path. "Clear no user state" is
          // v2.1's wording and it applies to the dead-access-token case as
          // much as to the live one. So the caller gets `null` meaning "no
          // usable token right now", never "signed out".
          //
          // And the re-auth is armed HERE — the clause this comment used to
          // defer to "another task's work" (#194 task 5). It is armed on BOTH
          // branches — a kept token that is still live gets its runway, and one
          // that is already dead re-authenticates at once (the delay clamps to
          // 0) rather than leaving the reader on a signed-in UI over a session
          // that can no longer be renewed.
          scheduleReauth();
          const kept = sessionStorage.getItem(TOKEN_KEY);
          const keptClaims = kept ? decodeJwtClaims(kept) : null;
          const keptExpMs = typeof keptClaims?.exp === 'number' ? keptClaims.exp * 1000 : null;
          return keptExpMs !== null && keptExpMs > serverNowMs() ? kept : null;
        };

        // #190 — the one check that stops a restored tab replaying a dead
        // token. Chrome's "reopen closed tab" restores this tab's
        // `sessionStorage` SNAPSHOT, so the value just read may be one the
        // original tab — now gone — already rotated away. Nothing in #184's
        // machinery can see that: there is no lock to contend for and no peer
        // left to broadcast to, so the restored tab's first refresh is a
        // sequential reuse.
        //
        // The whole mechanism is one storage asymmetry: a tab restore rolls
        // `sessionStorage` back and leaves `localStorage` alone, so the
        // tombstone is the only record that outlives the restore. The question
        // is never "how did this tab get here?" — a reload and a restore are
        // indistinguishable — but "is THIS TOKEN already dead?".
        //
        // `false` means "no tombstone found, OR the machinery to look is
        // unavailable" (insecure context, blocked site data). It is never
        // positive evidence the token is live, so it gates this refusal and
        // licenses nothing: everything below behaves exactly as before.
        //
        // A SOFT END, not a sign-out (#194): the tombstone says this REFRESH
        // token is dead, and says nothing at all about the access token beside
        // it. A restored tab whose access token has not yet expired keeps
        // working and re-authenticates when it does.
        if (await isRefreshTokenTombstoned(refreshToken)) return softEnd();

        const post = () =>
          fetch(`${config.guardiusUrl}/auth/token`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: refreshToken }),
            // No POST may hang inside the lock (#185).
            signal: timeoutSignal(REFRESH_ATTEMPT_TIMEOUT_MS),
          });

        // The attempt sequence. All of it runs inside the Web Lock and inside
        // the promise `refreshing.current` holds, so no wait below can ever
        // become a second holder of this refresh token (#184).
        //
        // `graceDeadline` is measured from the first AMBIGUOUS attempt, not
        // from the first attempt (ruling, Greg 2026-10-24). A 408/429 is
        // answered BEFORE the rotation, so no grace window can have opened
        // yet; a rejected fetch or a 5xx may have rotated, and the rotation
        // can only have happened at or after that attempt started, so
        // anchoring there is conservative.
        let graceDeadline: number | null = null;
        let lostReplyStep = 0;
        let usedGraceRetry = false;
        let usedRateRetry = false;

        /** Wait before re-presenting a token that MAY already have been
         *  rotated. False means the wait would carry us past the grace window:
         *  the caller must SOFT-END without sending anything, because a late
         *  presentation is judged theft and revokes every session the user
         *  holds. (It was a sign-out until #194 — giving the token up is what
         *  the safety property demands; giving the session up never was.) */
        const ambiguousWait = async (wait: number, attemptStartedAt: number): Promise<boolean> => {
          if (graceDeadline === null) graceDeadline = attemptStartedAt + REFRESH_GRACE_BUDGET_MS;
          if (Date.now() + wait > graceDeadline) return false;
          await sleep(wait);
          return true;
        };

        /** The lost-reply class: back off and re-present the same token, until
         *  the backoff is spent (then false → SOFT END). */
        const lostReplyWait = async (attemptStartedAt: number): Promise<boolean> => {
          const wait: number | undefined = REFRESH_LOST_REPLY_BACKOFF_MS[lostReplyStep++];
          if (wait === undefined) return false;
          return ambiguousWait(wait, attemptStartedAt);
        };

        for (;;) {
          // The grace bound, re-checked at the TOP of every attempt — before
          // anything is sent, and after any wait has actually resolved.
          //
          // `ambiguousWait`'s pre-check is NOT sufficient on its own, and this
          // is not redundant with it: that check only knows what the wait was
          // ASKED for. `sleep` is a `setTimeout`, and a `setTimeout` is not
          // punctual — Chrome throttles a hidden tab's timers to about once a
          // minute after ~5 min, and a timer pending across a lid-close
          // resolves whenever the machine wakes. So a 1 s sleep can resolve
          // with `Date.now()` arbitrarily far past `graceDeadline`, and the
          // pre-check has already said yes. Re-presenting a maybe-rotated
          // token outside Guardius's REFRESH_REUSE_GRACE_SECONDS is the
          // `theft` verdict: `revokeAllForSubject` + `401 TOKEN_REUSED`, every
          // session on every device. Checking here rather than after each
          // individual wait also covers every FUTURE wait anyone adds below.
          //
          // Keep BOTH checks. The pre-check avoids a pointless sleep (and is
          // what makes the tests' `clock.waits()` assertions mean anything);
          // this one is the actual budget.
          if (graceDeadline !== null && Date.now() > graceDeadline) return softEnd();
          const attemptStartedAt = Date.now();
          let res: Response;
          try {
            res = await post();
          } catch {
            // A rejected or aborted fetch: offline, CORS, a dropped
            // connection, or our own REFRESH_ATTEMPT_TIMEOUT_MS. We do not
            // know whether the server rotated, which is exactly why a retry is
            // correct AND why it is safe — re-presenting a rotated token
            // inside the grace window is graced, repeatedly, and a token that
            // was never rotated is simply still live.
            if (!(await lostReplyWait(attemptStartedAt))) return softEnd();
            continue;
          }

          if (res.ok) {
            let body: { access_token?: string; refresh_token?: string };
            try {
              body = (await res.json()) as { access_token?: string; refresh_token?: string };
            } catch {
              // A 2xx whose body we cannot read: unchanged from before #185,
              // the session dies rather than being retried.
              //
              // Still a HARD END after #194, and that is a ruling the v2.1
              // table does not quite make for us: it names "a 200 with no
              // access token" as a refusal and says nothing about a 200 whose
              // body will not parse. They are the same situation — the server
              // answered definitively and the answer contains no credential —
              // so both take the refusal path. Neither is an ambiguity: we
              // know the rotation happened and we know we did not get a usable
              // pair out of it.
              return killSession();
            }
            if (!body.access_token) return killSession();
            // #190: the token we just spent is dead the moment the server
            // answered 200 — the rotation has happened server-side whatever
            // this tab does next — so recording it is unconditionally safe and
            // goes FIRST, before the successor is stored and before the
            // rotation is published. Everything after this line can be lost to
            // a tab closing without creating a replayable token; losing THIS
            // line cannot. The window matters because a tab that dies inside
            // it leaves the predecessor untombstoned AND unpublished, so a
            // duplicated sibling still holding that predecessor in its own
            // `sessionStorage` presents it later — #190's revoke-all reached
            // through #184's residual gap. It also does not depend on when the
            // browser flushes a `sessionStorage` write to its session-restore
            // store, which is asynchronous and unobservable from here.
            //
            // AWAITED, unlike the other tombstone writes, and the distinction
            // is not stylistic: the user may close this tab in the very next
            // instant — which IS the restored-tab case — and a pending
            // microtask dies with the tab, leaving the one token that matters
            // most unrecorded. The digest is sub-millisecond and this runs
            // inside the #184 lock we are still holding, so it costs nothing
            // measurable.
            //
            // Guarded on the successor for the same reason the `setItem` below
            // is: without it, a 200 carrying no `refresh_token` would tombstone
            // the value still sitting in storage and the NEXT refresh would
            // sign the user out — a sign-out this code did not produce before
            // #190. That shape is unreachable today: Guardius's
            // `handleRefreshTokenGrant` (`server/src/auth/signin/token.ts`)
            // returns `refresh_token` as a plain unconditional key, and the
            // conditional spread `...(refreshToken ? { refresh_token } : {})`
            // nearby belongs to the AUTHORIZATION_CODE exchange, not to this
            // grant — so the adjacent `if` is itself dead on this path. The
            // guard is kept anyway and must not be "simplified" away: an
            // unguarded line beside a guarded one asserts a property of an
            // upstream service that nothing in our own request path checks.
            if (body.refresh_token) await tombstoneRefreshToken(refreshToken);
            // #210 — the session may have ENDED while this POST was in flight.
            // `signOut()` is synchronous and clears both keys plus React state;
            // so does the `signed-out` receiver when a sibling tab signs out.
            // Either can land at any yield between presenting the token and the
            // writes below, and then the four effects that follow re-seed
            // storage, flip the UI back to signed in, and hand the live
            // successor to every copy of this storage. On the `endSession` path
            // that is worst of all: `sessionStorage` SURVIVES the navigation to
            // Guardius, so the reader can come back to a signed-in Console
            // after a sign-out that really did end the hosted SSO session.
            //
            // The authority is the token value in storage, and this is the
            // broadcast receiver's OWN idiom, one function above at `:715`
            // (`if (sessionStorage.getItem(REFRESH_KEY) !== msg.spent) return;`)
            // and for the same reason: storage no longer holding the value this
            // attempt PRESENTED means this attempt no longer speaks for whatever
            // session that storage belongs to. Re-reading it needs no new state
            // and covers the cross-tab trigger for free — which an epoch counter
            // bumped at every clearing site would not.
            //
            // PLACEMENT: this must be the last statement after the LAST `await`
            // before the writes. Every yield before them — `await fetch`,
            // `await res.json()`, and the tombstone write just above — is an
            // interleaving point, so a check sitting above any of them is
            // already stale by the time the writes run. (A check above the
            // tombstone `await` would close the common case and reopen a
            // sub-millisecond window during the digest; the rule is about where
            // the check is GUARANTEED current, not about which window is
            // biggest.) Do not hoist it. **NOT PINNED BY A TEST, and said
            // rather than implied:** hoisting this above the tombstone `await`
            // leaves the suite fully green (measured on review), so the rule
            // lives in this comment alone. Pinning it needs a hand-resolved
            // deferral of the tombstone write with `signOut()` called while it
            // is pending; the bug a hoist reopens is a sub-millisecond digest
            // window, which is why that was judged not worth the machinery —
            // not because anything checks it.
            //
            // RESIDUAL, deliberate: the successor is NOT tombstoned and no
            // `/auth/logout` is sent for it. A tombstone would protect nothing
            // — the successor was never written to storage, so no restored tab
            // can hold it. What this branch leaves is a refresh token live at
            // Guardius for up to 7 days referenced by nothing: never stored,
            // never broadcast, never logged, existing only in this closure.
            //
            // The first version of this comment justified that by calling it
            // "strictly less exposed than the residual already accepted for
            // `endSession`'s best-effort revoke". **That premise is FALSE, and
            // it is corrected here rather than deleted because it reads as a
            // verified comparison:** on the one path this guard governs, that
            // revoke is not best-effort, it is a STRUCTURAL NO-OP. This guard
            // fires only when the rotation already completed server-side, so
            // the token `endSession` POSTs is already `status: 'revoked'`;
            // Guardius's `verifyRefreshToken` therefore throws, its logout
            // handler skips BOTH the single-token flip and the family revoke,
            // and it answers 200 having revoked no token at all. Tracked as
            // #219; Guardius accepted it upstream as
            // penseurai/penseur-guardius#1135 and is changing it server-side,
            // keyed on the signed `fid`, which needs nothing from this file.
            //
            // So the reason NOT to send a logout here is unchanged, but it now
            // stands on its own rather than on that comparison: this guard is
            // reached by INVOLUNTARY endings too — `queries.ts`'s 401, the
            // scheduled `signIn` fallback, the cross-tab `signed-out` receiver
            // — and a logout POST revokes the FAMILY, which would end a
            // duplicated sibling tab's session over one transient 401. That is
            // the "pointing a 401 at `endSession` is worse than #200" rule
            // reached by another door. Guardius has also asked us not to.
            if (sessionStorage.getItem(REFRESH_KEY) !== refreshToken) return null;
            // Rotation: persist the NEW refresh token (and overwrite the spent
            // one) BEFORE anything else can read it. A reused refresh token
            // revokes all sessions, so the old value must never survive a
            // successful refresh.
            //
            // #289: learn the clock here, AFTER the #210 guard above — a 200
            // discarded because the session ended must not re-seed the offset
            // the sign-out just cleared. Residual, accepted: a 200 handled after
            // the tab froze mid-fetch carries the same latency bias the
            // `rotated` receiver refuses to learn, accepted here because a
            // connection frozen that long usually dies before the reply lands.
            learnClockFrom(body.access_token);
            sessionStorage.setItem(TOKEN_KEY, body.access_token);
            if (body.refresh_token) sessionStorage.setItem(REFRESH_KEY, body.refresh_token);
            setToken(body.access_token);
            // Hand the successor to the copies of this storage, keyed on the
            // token just spent. Without this a duplicated tab is left holding a
            // dead token and dies on its next refresh.
            publish({
              type: 'rotated',
              spent: refreshToken,
              accessToken: body.access_token,
              refreshToken: body.refresh_token ?? null,
            });
            return body.access_token;
          }

          if (REFRESH_RETRYABLE_STATUS.has(res.status)) {
            // #194 delta 3 — a 408/429 reached AFTER an ambiguous attempt ENDS
            // THE RUN. v1 instead bounded the retry wait by the grace deadline
            // and gave up only if the wait would overrun it, and answered a
            // second 429 with `killSession()`. Both readings are safe; v2.1
            // chose the simpler rule across all three apps and we accepted it,
            // so what changed is not the safety argument but who makes it: one
            // predicate here, rather than deadline arithmetic on this path and
            // its own sibling exit below.
            //
            // Why the predicate is the right one, unchanged from #185: #188's
            // "a 429 means the token was never presented to the rotation" is
            // true of THIS attempt only. An earlier rejected fetch or 5xx may
            // already have rotated it, so the stored value is maybe-spent, and
            // re-presenting it later — minutes later, long outside the grace
            // window — is theft and revokes every session on every device.
            // Dropping one token is the safe direction, and after #194 it
            // costs only the token, not the session.
            //
            // The deadline arithmetic this replaced is NOT a loosening of the
            // grace bound: ending the run is strictly more conservative than
            // waiting out a bounded wait and re-presenting.
            if (graceDeadline !== null) return softEnd();
            // Rate-limited or timed out: the token was never presented to the
            // rotation, so it is still live. Keep BOTH tokens, touch no React
            // state, wait and re-send the SAME value exactly once.
            // KEEP (v2.1), and unchanged by #194: every line from here down is
            // reached only with `graceDeadline === null`, i.e. nothing
            // ambiguous has happened in this sequence, so the token demonstrably
            // never reached a rotation and is still live. #188's behaviour
            // exactly — both tokens kept, no React state touched, the SAME
            // value re-sent once after a clamped `Retry-After`, awaited inside
            // the single-flight promise so the wait can never become a second
            // holder.
            if (usedRateRetry) {
              // Still rate-limited: no fresh token to hand back, but the
              // session is alive. `null` is the contract's "no token valid for
              // >= the skew", NOT "signed out" — callers must not clear
              // anything on it.
              return null;
            }
            usedRateRetry = true;
            // Ungated by the grace deadline, and now trivially so: there is no
            // deadline on this path. A 408/429 is answered BEFORE the rotation,
            // so no grace window can have opened, and the one that might have
            // opened earlier in the sequence was handled by the SOFT END above
            // rather than by arithmetic here.
            await sleep(refreshRetryDelayMs(res));
            continue;
          }

          if (res.status >= 500) {
            const code = await failureCode(res);
            // Exactly their SDK's gate: 503 AND one of the two codes. One
            // retry, then the refresh token is gone — it was rotated and we
            // could not collect its successor.
            //
            // Both exits are SOFT ENDs after #194 (ruling: a 503 is an
            // ambiguity, not a refusal — the server never told us this
            // credential was finished, only that it could not finish the
            // rotation). v2.1's HARD END list is refusals, and a 503 is on
            // neither of its 4xx rows.
            if (res.status === 503 && code !== undefined && REFRESH_GRACE_CODES.has(code)) {
              if (usedGraceRetry) return softEnd();
              usedGraceRetry = true;
              if (!(await ambiguousWait(graceRetryDelayMs(res), attemptStartedAt))) {
                return softEnd();
              }
              continue;
            }
            // Any other 5xx, including a 503 with no usable code: lost reply.
            if (!(await lostReplyWait(attemptStartedAt))) return softEnd();
            continue;
          }

          // Every 4xx that is not 408/429 — `401 TOKEN_REUSED` above all.
          // Never retried: that retry IS the reuse.
          return killSession();
        }
      });

    const promise = run().finally(() => {
      refreshing.current = null;
    });
    refreshing.current = promise;
    return promise;
  };

  // The method the API layer calls before each request: hand back the best
  // token available — one valid for at least EXPIRY_SKEW_MS where it can get
  // one, refreshing silently if not, else one that is merely unexpired.
  const ensureFreshToken = async (): Promise<string | null> => {
    const current = sessionStorage.getItem(TOKEN_KEY);
    const refreshToken = sessionStorage.getItem(REFRESH_KEY);
    // Logged out and nothing to refresh from.
    if (!current && !refreshToken) return null;

    const claims = current ? decodeJwtClaims(current) : null;
    const expMs = typeof claims?.exp === 'number' ? claims.exp * 1000 : null;
    const stale = expMs === null || expMs - serverNowMs() <= EXPIRY_SKEW_MS;
    // Stale, and the contract is NOT "valid for ≥ EXPIRY_SKEW_MS or null" any
    // more (#194 delta 4). It is "the best token available", and the floor is
    // UNEXPIRED rather than fresh: a token inside the skew still verifies, so
    // withholding it fails the request for nothing.
    //
    // With a refresh token, try for a fresh one. `refreshSession` may itself
    // answer with a token inside the skew — that is the SOFT END, which keeps
    // the access token and hands it straight back through this line, and is
    // why this accessor had to change rather than gaining a branch of its own.
    if (stale) {
      if (refreshToken) return refreshSession();
      // No refresh token: either a plain sign-out (no access token either,
      // handled above) or a SOFT END. The access token outlives the refresh
      // token it was issued beside, and v2.1 says to keep using it until its
      // own `exp`. Reporting null here would hide a token that still verifies
      // and sign the reader out of a session Guardius would still serve.
      //
      // Still `null` for an undecodable `exp` (`expMs === null`) — unchanged,
      // and deliberate: we cannot say that one verifies.
      return expMs !== null && expMs > serverNowMs() ? current : null;
    }
    return current;
  };

  // Proactive refresh timer: keep an idle tab's session alive so background
  // react-query refetches never hit an expired token. Schedule a refresh at
  // (exp - EXPIRY_SKEW_MS); re-armed whenever `token` changes (a fresh token
  // resets the clock) and cleared on unmount. Background-tab throttling can
  // defer this setTimeout — the visibility top-up below recovers that case.
  useEffect(() => {
    if (!token) return;
    const claims = decodeJwtClaims(token);
    if (typeof claims?.exp !== 'number') return;
    const delay = Math.max(0, claims.exp * 1000 - serverNowMs() - EXPIRY_SKEW_MS);
    const id = setTimeout(() => {
      void refreshSession();
    }, delay);
    return () => clearTimeout(id);
    // refreshSession reads live storage and is single-flighted, so it's safe to
    // omit from deps; re-arming is driven solely by token rotation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Visibility top-up: when a backgrounded/slept tab becomes visible again, the
  // proactive setTimeout may have been throttled past expiry. Re-check on wake
  // and refresh if needed. Fire-and-forget; single-flight dedupes against any
  // in-flight proactive refresh.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void ensureFreshToken().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (): Promise<void> => {
    setError(null);
    const { verifier, challenge } = await generatePkce();
    const state = randomState();
    const redirectUri = window.location.origin + redirectPath;
    // Where the reader is, so the callback can put them back (#194). All three
    // parts: dropping `search` or `hash` lands them on the right page with the
    // wrong view selected, which is the half of "save the user's place" that is
    // easy to miss. Recorded for EVERY sign-in, not only a scheduled re-auth —
    // a reader who clicks "sign in" from a deep link deserves the same, and one
    // code path is one fewer thing to get wrong.
    const returnTo = window.location.pathname + window.location.search + window.location.hash;
    const pending: PendingAuth = { verifier, state, redirectUri, returnTo };
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    // Pass the management tenant so the minted token's `aud` matches what the
    // Watchtower Lambdas require.
    const authorizeUrl = buildAuthorizeUrl(config, { challenge, state, redirectUri });
    window.location.assign(authorizeUrl);
  };

  /** The LOCAL half of ending a session: this tab's storage, this provider's
   *  state, this tab's timers. It tells Guardius NOTHING — the refresh token
   *  stays valid until its own `exp` and the hosted SSO session stays alive —
   *  which is correct for an involuntary ending and WRONG for a deliberate
   *  one. `endSession()` is the deliberate-sign-out call; it wraps this. */
  const signOut = (): void => {
    // #190: read the refresh token BEFORE discarding it, so the value this tab
    // is throwing away can be recorded. A tab restored from before the
    // sign-out would otherwise replay it. Fire-and-forget — `signOut` is a
    // synchronous void by contract and nothing here reads the tombstone back.
    const discarded = sessionStorage.getItem(REFRESH_KEY);
    if (discarded) {
      void tombstoneRefreshToken(discarded);
      // #198: tell the copies. Without this a duplicated tab kept a signed-in
      // UI until its own next refresh (~14 min). `signed-out`, never
      // `drop-this-token` — see the `AuthBroadcast` doc above: only
      // `signed-out` says "and the session is over", which is what this is.
      // Guarded on `discarded`, since an unkeyed message names no sign-in.
      publish({ type: 'signed-out', spent: discarded });
    }
    // #194: a re-auth an earlier SOFT END armed must not survive an explicit
    // sign-out. Leaving it would navigate the reader to the authorize endpoint
    // during the runway and sign them straight back in — the one way this
    // trigger could override a deliberate user action.
    cancelScheduledReauth();
    sessionStorage.removeItem(PENDING_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    clearServerOffset();
    sessionStorage.removeItem(REFRESH_KEY);
    setToken(null);
    setError(null);
    // Clear the server-denied latch so a subsequent sign-in re-evaluates access.
    setServerDenied(false);
  };

  /** #211. The run in flight, or `null` before the first one. Deliberately
   *  NEVER cleared: `endSession` ends in a top-level navigation, so there is
   *  no "after" for a second run to belong to. An uncleared ref here is the
   *  design, not a leak.
   *
   *  "No after" is the reason on the NAVIGATING path and is not the whole
   *  reason, which is worth stating so a reader who meets the other path finds
   *  this comment incomplete rather than false: `run` REJECTS if `signOut()`
   *  or `assign` throws, and then the page is still here and every later click
   *  gets back the same rejected promise instead of retrying. Left as is on
   *  purpose — a retry would hit the same throw. Reaching it needs a throwing
   *  `sessionStorage` (an app that cannot boot either) or a malformed
   *  `config.guardiusUrl` (which `signIn` builds from the same expression, so
   *  the reader could not have signed in). Both call sites `void` the result,
   *  so the one visible consequence is an unhandled rejection re-reported on
   *  each later click. */
  const endSessionRun = useRef<Promise<void> | null>(null);

  /** #200. See `AuthContextValue.endSession` for what this is for and why
   *  `signOut()` alone is not enough. The order here is the whole design.
   *
   *  #211: both buttons are wired `onClick={() => void endSession()}`, so a
   *  double click calls this twice. The second call must NOT run the body
   *  again: by then `signOut()` has cleared `REFRESH_KEY`, so step 1 reads
   *  `null`, both entries of step 3's `Promise.allSettled` are
   *  already-resolved promises, and step 4 navigates on the next microtask —
   *  POSSIBLY aborting the first call's in-flight logout POST and CERTAINLY
   *  abandoning its pending tombstone write, which is exactly the property
   *  step 3's `await` exists to buy. That asymmetry is the right way round and
   *  an earlier revision had it backwards: the POST carries `keepalive: true`,
   *  whose whole job is to survive a navigation (merely unreliably — see its
   *  own note below), whereas the tombstone write is a `crypto.subtle`
   *  microtask chain a navigation kills every time. So a re-entrant call returns the STORED promise rather than
   *  `undefined`: a second `void endSession()` then awaits the same work, and
   *  so does any caller that awaits the return value.
   *
   *  Not `async`, deliberately: an `async` wrapper returning `endSessionRun`
   *  would flatten it into a fresh promise, so `second === first` would be
   *  false and a re-entrant caller would be awaiting a copy. */
  const endSession = (): Promise<void> => {
    if (endSessionRun.current) return endSessionRun.current;
    const run = (async (): Promise<void> => {
      // 1. BEFORE `signOut()`, which removes it. This is the value both of the
      //    remaining steps need.
      const refreshToken = sessionStorage.getItem(REFRESH_KEY);

      // 2. The local teardown first, so the UI responds instantly — preserving
      //    the feel of today's button — and so the `cancelScheduledReauth()` /
      //    `removeItem(REFRESH_KEY)` adjacency is INHERITED rather than copied.
      //    (That adjacency is what stops a SOFT END's armed re-auth navigating
      //    the reader back into the session they just left; with a live hosted
      //    SSO session it would be a silent `302` they never see, which is this
      //    issue's own failure read from the inside.)
      signOut();

      // 3. Awaited, because this tab is about to die. `tombstoneRefreshToken`
      //    is async (`crypto.subtle`), and `signOut()`'s fire-and-forget write
      //    would be a dropped microtask the moment we navigate — leaving the
      //    discarded token unrecorded for a restored tab to replay, which is
      //    #190 reopened by this very call. Writing it a second time is a
      //    de-dupe in that module, not a duplicate entry.
      //
      //    `allSettled`, NEVER `all`: neither of these may stop step 4.
      await Promise.allSettled([
        refreshToken ? tombstoneRefreshToken(refreshToken) : Promise.resolve(),
        // Nothing to revoke without a token, so no POST at all — and the
        // navigation still happens, because the SSO session is independent of
        // whether this tab still held a refresh token.
        refreshToken
          ? fetch(`${config.guardiusUrl}/auth/logout`, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ refreshToken }),
              // Browser `fetch` has no timeout; a down Guardius must not hold
              // the reader on a signed-in-looking UI.
              signal: timeoutSignal(END_SESSION_LOGOUT_TIMEOUT_MS),
              // A BONUS, never the mechanism: a JSON body triggers a CORS
              // preflight, and `keepalive` + preflight has had browser gaps.
              // The awaited, bounded call above is what actually delivers this.
              keepalive: true,
            }).catch(() => {})
          : Promise.resolve(),
      ]);

      // 4. UNCONDITIONAL. Ending the hosted SSO session is the security-critical
      //    half and the one a `fetch` cannot do — the preflight on
      //    `/auth/logout` carries no `Access-Control-Allow-Credentials`, so no
      //    cross-origin `fetch` may send the cookie; `SameSite` is NOT the
      //    reason (see `AGENTS.md`).
      //    Guardius revokes the session before it validates the return address
      //    and before its own rate-limit gate, so there is no failure above that
      //    makes NOT navigating safer — and the refresh token is already
      //    tombstoned and locally gone either way.
      //
      //    The return address is built with the SAME expression `signIn` uses,
      //    so `post_logout_redirect_uri` is a URI Guardius already has
      //    registered (it runs the authorize flow's own `validateRedirectUri`).
      window.location.assign(
        buildEndSessionUrl(config, {
          postLogoutRedirectUri: window.location.origin + redirectPath,
        }),
      );
    })();
    // Assigned after `run()` returns, which is after the body's synchronous
    // prefix (steps 1-3 up to the first `await`) has already executed. That
    // satisfies "set before step 1" in the only sense that can matter, on two
    // facts and no others: nothing in that prefix CALLS `endSession` (a
    // synchronous re-render would rebuild this closure, never invoke it), and
    // no microtask can interleave between `run()` returning and the line
    // below. A deferred promise to make the ordering literal would be new
    // machinery guarding a window that does not exist.
    endSessionRun.current = run;
    return run;
  };

  const denyAccess = (): void => setServerDenied(true);

  const { address, canRead } = deriveState(token);

  const value: AuthContextValue = {
    config,
    token,
    address,
    canRead,
    serverDenied,
    denyAccess,
    error,
    signIn,
    signOut,
    endSession,
    ensureFreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

/** Strip the OAuth callback query (code/state) from the URL without a reload. */
function stripQuery(): void {
  window.history.replaceState({}, '', window.location.pathname + window.location.hash);
}
